// Idle watch lato client: rileva inattività reale (10 minuti) e notifica il
// chiamante per fare logout/lock. Non è l'unico enforcement: il backend
// applica lo stesso idle timeout in modo stateless via sliding cookie (vedi
// functions/_middleware.ts), quindi una sessione resta scaduta anche se la
// tab viene chiusa e riaperta dopo più di 10 minuti. Questo modulo serve a
// reagire mentre la tab è aperta (logout immediato, pulizia UI) e a generare
// un heartbeat minimo quando l'utente è attivo solo lato client (es. legge la
// mappa senza fare chiamate API), così l'enforcement server-side non scade
// per mancanza di richieste pur in presenza di attività reale.

import { getSession } from "./api";

export const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const CHECK_INTERVAL_MS = 20 * 1000;
// Heartbeat throttle: non più frequente di una volta al minuto, e solo se è
// stata rilevata attività dopo l'ultimo heartbeat (altrimenti un heartbeat
// "a tempo" allungherebbe la sessione anche durante l'inattività, vanificando
// il timeout).
const HEARTBEAT_MIN_INTERVAL_MS = 60 * 1000;

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "mousemove"] as const;

/** Avvia il watch; ritorna una funzione di stop (rimuove listener/interval). */
export function startIdleWatch(onIdle: () => void): () => void {
  let lastActivityAt = Date.now();
  let lastHeartbeatAt = Date.now();
  let stopped = false;

  const markActivity = () => {
    lastActivityAt = Date.now();
  };

  const onVisibility = () => {
    if (document.visibilityState === "visible") markActivity();
  };

  for (const ev of ACTIVITY_EVENTS) {
    document.addEventListener(ev, markActivity, { passive: true });
  }
  document.addEventListener("visibilitychange", onVisibility);

  const interval = setInterval(() => {
    if (stopped) return;
    const now = Date.now();
    if (now - lastActivityAt >= IDLE_TIMEOUT_MS) {
      stop();
      onIdle();
      return;
    }
    if (lastActivityAt > lastHeartbeatAt && now - lastHeartbeatAt >= HEARTBEAT_MIN_INTERVAL_MS) {
      lastHeartbeatAt = now;
      // Fire-and-forget: l'unico scopo è il side effect server-side (reissue
      // del cookie via middleware). Un fallimento di rete non deve avere
      // alcun effetto visibile lato client.
      getSession().catch(() => {});
    }
  }, CHECK_INTERVAL_MS);

  function stop(): void {
    if (stopped) return;
    stopped = true;
    clearInterval(interval);
    for (const ev of ACTIVITY_EVENTS) {
      document.removeEventListener(ev, markActivity);
    }
    document.removeEventListener("visibilitychange", onVisibility);
  }

  return stop;
}
